/**
 * Client Error Telemetry — Phase 0.3
 *
 * Reports client-side errors (render, unhandled, api) to the gateway
 * `/api/telemetry/client-error` endpoint. Debounced 5s per `kind+url+name`
 * tuple so a tight retry-loop doesn't flood logs.
 *
 * The payload lands in `vhire_telemetry` via the gateway's Tail Worker
 * (the gateway writes a `console.error(JSON.stringify(...))` on receive;
 * the Tail Worker extracts `primaryLogEvent`).
 *
 * Usage:
 *   import { reportClientError } from '@/lib/telemetry/client-error';
 *   reportClientError({ kind: 'render', error, context: { sessionId } });
 */

export type ClientErrorKind = 'render' | 'unhandled' | 'api-error';

export interface ClientErrorPayload {
  kind: ClientErrorKind;
  /** Path / URL where the error happened. */
  url: string;
  /** HTTP status if this was an api-error, else undefined. */
  status?: number;
  /** Constructor name of the error (TypeError, SyntaxError, etc.). */
  errorName?: string;
  /** Error message, truncated at 512 chars. */
  errorMessage?: string;
  /** Stack trace, truncated at 2048 chars. */
  errorStack?: string;
  /** Optional business context (sessionId, tenantId, etc.). */
  context?: Record<string, unknown>;
  /** Set automatically — don't pass. */
  userAgent?: string;
}

/** The signed-in principal, as opaque IDs only. Never an email, name or phone. */
export interface TelemetryIdentity {
  tenantId?: string | null;
  userId?: string | null;
  role?: string | null;
}

/** What `describeUnknownError` makes of anything that can be thrown or rejected. */
export interface DescribedError {
  name: string;
  message: string;
  stack?: string;
}

const MAX_MESSAGE = 512;
const MAX_STACK = 2048;
const MAX_RESOURCE_URL = 160;
const MAX_NON_ERROR_JSON = 200;
const MAX_CONTEXT_ID = 128;
const DEBOUNCE_WINDOW_MS = 5000;

/**
 * Identity merged into every payload's `context`. Set by the auth contexts
 * (`setTelemetryIdentity`) so a report says WHICH tenant/user hit the error
 * without every call site having to know.
 */
let telemetryIdentity: TelemetryIdentity | null = null;

/** Keyed by `kind:url:errorName` → last-sent timestamp. */
const debounceMap = new Map<string, number>();

/** Resolved from NEXT_PUBLIC_API_URL at bundle time. */
import { API_BASE } from '@/lib/config';
// Public routes whose path carries a live credential (`/claim/<claim JWT>`,
// `/agreement/<signer link token>`, `/api/agreement-links/<token>/…`, …) are
// ONE list, shared with the gateway's request log and the error tracker.
// Telemetry is durable (`vhire_telemetry`), so the URL is redacted before it
// is used for anything: the credential segment becomes `[token]` and the query
// goes; API paths that merely contain a route word keep their full shape.
import { redactTokenPaths } from '@vhire/shared/redact-token-paths';

function truncate(s: string | undefined, max: number): string | undefined {
  if (!s) return undefined;
  return s.length <= max ? s : s.slice(0, max);
}

/** An email in an error message or stack is PII; ids and codes survive. */
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

function scrubEmails(s: string): string {
  return s.replace(EMAIL_PATTERN, '[email]');
}

/**
 * A URL that is safe to put in a durable payload: query string and fragment
 * gone first (a signed or tokenised query must never survive), the credential
 * path segment redacted, THEN truncated — so a cut can never split a token
 * into something the redactor no longer recognises. `data:` / `blob:` URLs
 * carry content, not a location, so only their scheme is kept.
 */
function safeUrlForTelemetry(raw: string, max = MAX_RESOURCE_URL): string {
  const opaque = /^(data|blob):/i.exec(raw);
  if (opaque) return opaque[0].toLowerCase();
  const withoutQuery = raw.replace(/[?#][\s\S]*$/, '');
  return redactTokenPaths(withoutQuery).slice(0, max);
}

/** The `src` (or `href`) of an event target, made safe. */
function targetUrl(target: unknown): string | undefined {
  if (typeof target !== 'object' || target === null) return undefined;
  const { src, href } = target as { src?: unknown; href?: unknown };
  const raw = typeof src === 'string' && src ? src : typeof href === 'string' && href ? href : '';
  return raw ? safeUrlForTelemetry(raw) : undefined;
}

function describeEventTarget(target: unknown): string {
  if (typeof target !== 'object' || target === null) return 'UNKNOWN';
  const { tagName } = target as { tagName?: unknown };
  if (typeof tagName === 'string' && tagName) return tagName.toUpperCase();
  const ctorName = (target as { constructor?: { name?: string } }).constructor?.name;
  // `target === window` is not reliable across realms (jsdom, iframes), the
  // constructor name is.
  if ((typeof window !== 'undefined' && target === window) || ctorName === 'Window') return 'WINDOW';
  return ctorName || 'UNKNOWN';
}

function isEventLike(v: unknown): v is { type: string; target: unknown } {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { type?: unknown }).type === 'string' &&
    'target' in v
  );
}

function isErrorLike(v: unknown): v is { name: string; message: string; stack?: unknown } {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { name?: unknown }).name === 'string' &&
    typeof (v as { message?: unknown }).message === 'string'
  );
}

function withStack(name: string, message: string, stack: unknown): DescribedError {
  const described: DescribedError = { name, message: scrubEmails(message) };
  if (typeof stack === 'string' && stack) described.stack = scrubEmails(stack);
  return described;
}

/**
 * Turn anything that can be thrown or used to reject a promise into a
 * name / message / stack that says what actually happened.
 *
 * `String(reason)` is what made a rejected DOM Event land as "[object Event]"
 * (60 issues on /jobs pages and the only browser-origin signal in production).
 * An Event now says which event hit which element and, for a failed load, which
 * URL (query stripped, credential paths redacted). Never throws.
 */
export function describeUnknownError(reason: unknown): DescribedError {
  try {
    if (reason instanceof Error) {
      return withStack(reason.name || reason.constructor?.name || 'Error', reason.message, reason.stack);
    }
    if (reason === null || reason === undefined) {
      return { name: 'Unknown', message: 'unknown rejection' };
    }
    if (typeof reason === 'string') {
      return { name: 'Error', message: scrubEmails(reason) };
    }
    // An error from another realm (iframe, worker) fails `instanceof Error`
    // but still carries name + message — keep them rather than JSON-dumping it.
    if (isErrorLike(reason)) {
      return withStack(reason.name || 'Error', reason.message, reason.stack);
    }
    if (isEventLike(reason)) {
      const type = reason.type.slice(0, 40);
      const src = targetUrl(reason.target);
      return {
        name: 'UnhandledEvent',
        message: `${type} event on ${describeEventTarget(reason.target)}${src ? ` src=${src}` : ''}`,
      };
    }
    if (typeof reason === 'object') {
      let json: string | undefined;
      try {
        json = JSON.stringify(reason);
      } catch {
        json = undefined; // cycle, BigInt, throwing toJSON …
      }
      return {
        name: 'NonError',
        message: scrubEmails(json ?? Object.prototype.toString.call(reason)).slice(0, MAX_NON_ERROR_JSON),
      };
    }
    return { name: 'NonError', message: scrubEmails(String(reason)).slice(0, MAX_NON_ERROR_JSON) };
  } catch {
    return { name: 'Unknown', message: 'unreadable error' };
  }
}

/** An opaque id or code for `context`; anything that looks like an email is dropped. */
function cleanContextId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('@')) return undefined;
  return trimmed.slice(0, MAX_CONTEXT_ID);
}

/**
 * Record who is signed in so every later report carries `tenantId`, `userId`
 * and `role` in its `context`. Opaque ids only — never an email or a name.
 * Pass `null` on logout. Fields the caller passes in `context` still win.
 */
export function setTelemetryIdentity(identity: TelemetryIdentity | null): void {
  if (!identity) {
    telemetryIdentity = null;
    return;
  }
  const next: TelemetryIdentity = {
    tenantId: cleanContextId(identity.tenantId) ?? null,
    userId: cleanContextId(identity.userId) ?? null,
    role: cleanContextId(identity.role) ?? null,
  };
  telemetryIdentity = next.tenantId || next.userId || next.role ? next : null;
}

function buildContext(
  base: Record<string, unknown> | undefined,
  requestId: string | undefined,
): Record<string, unknown> | undefined {
  const merged: Record<string, unknown> = {};
  if (telemetryIdentity) {
    if (telemetryIdentity.tenantId) merged.tenantId = telemetryIdentity.tenantId;
    if (telemetryIdentity.userId) merged.userId = telemetryIdentity.userId;
    if (telemetryIdentity.role) merged.role = telemetryIdentity.role;
  }
  if (base) Object.assign(merged, base);
  const rid = cleanContextId(requestId);
  if (rid) merged.requestId = rid;
  return Object.keys(merged).length > 0 ? merged : undefined;
}

function shouldSend(key: string): boolean {
  const now = Date.now();
  const last = debounceMap.get(key);
  if (last && now - last < DEBOUNCE_WINDOW_MS) {
    return false;
  }
  debounceMap.set(key, now);
  // Cheap LRU: cap map at 200 entries. When it grows, drop the oldest.
  if (debounceMap.size > 200) {
    const firstKey = debounceMap.keys().next().value;
    if (firstKey !== undefined) debounceMap.delete(firstKey);
  }
  return true;
}

/**
 * Fire-and-forget error report. Never throws, never blocks.
 *
 * On the browser we use `navigator.sendBeacon` when available — survives
 * the page being unloaded (crucial for render errors that navigate away).
 * Falls back to `fetch()` with `keepalive: true`.
 */
export function reportClientError(input: {
  kind: ClientErrorKind;
  error?: Error | unknown;
  status?: number;
  url?: string;
  context?: Record<string, unknown>;
  /** The gateway's `x-request-id` for the failing call, when the browser could read it. */
  requestId?: string;
}): void {
  try {
    const described = input.error === undefined ? undefined : describeUnknownError(input.error);
    const errorName = described?.name;
    const errorMessage = described?.message;
    const errorStack = described?.stack;

    // Redacted BEFORE anything else touches it — the debounce key, the payload
    // and any future consumer all see the safe form, never the credential.
    const url = redactTokenPaths(
      input.url ??
        (typeof window !== 'undefined' ? window.location.pathname : ''),
    );

    const key = `${input.kind}:${url}:${errorName ?? 'unknown'}`;
    if (!shouldSend(key)) return;

    const payload: ClientErrorPayload = {
      kind: input.kind,
      url,
      status: input.status,
      errorName,
      errorMessage: truncate(errorMessage, MAX_MESSAGE),
      errorStack: truncate(errorStack, MAX_STACK),
      context: buildContext(input.context, input.requestId),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };

    const endpoint = `${API_BASE}/telemetry/client-error`;
    const body = JSON.stringify(payload);

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // sendBeacon is best-effort but survives page unload.
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }

    // Fallback — keepalive lets the request continue past unload.
    void fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Swallow. Telemetry must never break the page.
    });
  } catch {
    // Any serialization / URL failure: silent drop.
  }
}

/**
 * Install global window error + unhandled-rejection listeners. Idempotent.
 * Call once, from a client component mounted in the root layout.
 */
export function installClientErrorHooks(): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { __vhireErrorHooksInstalled?: boolean };
  if (w.__vhireErrorHooksInstalled) return;
  w.__vhireErrorHooksInstalled = true;

  // Capture phase: a failed <script>/<img>/<link> load fires an `error` event
  // that does NOT bubble, so only a capturing window listener ever sees it.
  // A thrown exception is dispatched AT window, where this single listener runs
  // exactly once — it is reported once, not once per phase.
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target;
      if (typeof Element !== 'undefined' && target instanceof Element) {
        const tag = target.tagName.toUpperCase();
        // A plain name/message object, not `new Error(...)`: a synthetic stack
        // would only point at this listener.
        reportClientError({
          kind: 'unhandled',
          error: { name: 'ResourceLoadError', message: `resource load failed: ${tag}` },
          context: { source: 'resource', tag, src: targetUrl(target) },
        });
        return;
      }
      reportClientError({
        kind: 'unhandled',
        error: describeUnknownError(event.error ?? new Error(event.message)),
        context: {
          // An inline script reports the PAGE url as its filename — that can be
          // a credential route — so it is made safe like every other url.
          filename: event.filename ? safeUrlForTelemetry(event.filename) : event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    },
    true,
  );

  window.addEventListener('unhandledrejection', (event) => {
    reportClientError({
      kind: 'unhandled',
      error: describeUnknownError(event.reason),
      context: { source: 'unhandledrejection' },
    });
  });
}
